import React, { useMemo } from 'react';
import { observer } from 'mobx-react-lite';

import Maintenance from '../maintenance';
import { useStore } from '../../../hooks';
import { ErrorPage } from '../error-page';
import { ComingSoon } from '../coming-soon';
import { failLaunch, markLaunchStep, recordFrameTiming } from '../../../services/launch-telemetry';

interface GameFrameProps {
  frameNonce?: number;
  refCallback: React.Ref<HTMLIFrameElement>;
  frameSource: string;
}

export const GameFrame: React.FC<GameFrameProps> = observer(({ refCallback, frameSource, frameNonce = 0 }) => {
  const { gameStore, loaderStore, userActivityStore, maintenanceStore } = useStore();
  const { launchData: { data: launchData }, gameErrorInfo } = gameStore;
  const { gameLoaded } = loaderStore;
  const isComingSoon = launchData?.isComingSoon;
  const isError = !!gameErrorInfo;
  const isMaintanance = maintenanceStore.isMaintenanceMode;

  const content = useMemo(() => {
    if (isError) return <ErrorPage />;
    if (isMaintanance) return <Maintenance />;
    if (isComingSoon) return <ComingSoon />;
    return (
      <>
        {frameSource ? (
          <iframe
            // key forces a new element, so a dead document is really dropped on retry
            key={frameNonce}
            ref={refCallback}
            src={frameSource}
            className="game-frame"
            title="Pascal Gaming Game Frame"
            onLoad={event => {
              // Always, even after finishLaunch: the retry gate in GameLaunch reads this flag, while telemetry
              // stops recording once the launch is finished.
              loaderStore.setFrameLoaded(true);
              markLaunchStep('iframeLoaded');
              userActivityStore.markLoad('iframeLoaded');
              recordFrameTiming(event.currentTarget);
            }}
            onError={() => failLaunch('iframe', 'iframe error event')}
            allow="fullscreen; clipboard-read; clipboard-write"
            sandbox="allow-popups allow-popups-to-escape-sandbox allow-same-origin allow-forms allow-scripts allow-top-navigation"
          />
        ) : null}
      </>
    );
  }, [isError, isMaintanance, frameSource, frameNonce, isComingSoon, refCallback, gameLoaded]);

  return (
    <>{content}</>
  );
});
